import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, BookOpen, Save } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import {
  Card, Field, Input, Select, Textarea, Button, Spinner,
} from "@/components/ui";

// =============================================================================
// NewRateCardScreen — /rates/new
// Create a new rate card and land on its detail screen so the user can start
// journalling lines straight away.
//
// Three card types (matching the rate_card_type values used elsewhere in the
// app — see QuoteBuilderScreen.tsx):
//   • coretel_sor  — Coretel master sell-side card
//   • supplier_sor — supplier cost-side card (what they charge us)
//   • client_sor   — client-specific overrides of the master
//
// Supplier and client selectors only show when their card type is picked.
// =============================================================================

type CardType = "coretel_sor" | "supplier_sor" | "client_sor";

interface SupplierOption {
  supplier_id:  string;
  legal_name:   string;
  trading_name: string | null;
  supplier_code: string;
}
interface ClientOption {
  client_id:    string;
  legal_name:   string;
  client_code:  string;
}

// -----------------------------------------------------------------------------

export default function NewRateCardScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const canManage = hasRole(user, "manager");

  const [cardType, setCardType]       = useState<CardType>("supplier_sor");
  const [supplierId, setSupplierId]   = useState<string>("");
  const [clientId, setClientId]       = useState<string>("");
  const [code, setCode]               = useState<string>("");
  const [codeTouched, setCodeTouched] = useState(false);
  const [displayName, setDisplayName] = useState<string>("");
  const [nameTouched, setNameTouched] = useState(false);
  const [effectiveFrom, setEffectiveFrom] = useState<string>(
    () => new Date().toISOString().slice(0, 10)
  );
  const [description, setDescription] = useState<string>("");
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_new_rate_card"],
    enabled:  canManage,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("suppliers")
        .select("supplier_id, legal_name, trading_name, supplier_code")
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as unknown as SupplierOption[];
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ["clients_for_new_rate_card"],
    enabled:  canManage,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from("clients")
        .select("client_id, legal_name, client_code")
        .eq("is_active", true)
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as unknown as ClientOption[];
    },
  });

  // Auto-suggest code + display_name based on the picked party. User can
  // override by typing in either field; once they touch one, we stop
  // auto-overwriting it.
  const yearStr = String(new Date().getFullYear());
  const autoCode = useMemo(() => {
    if (cardType === "supplier_sor" && supplierId) {
      const s = suppliers.find(s => s.supplier_id === supplierId);
      if (s) {
        const slug = (s.trading_name ?? s.legal_name)
          .toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "")
          .slice(0, 24);
        return `${slug}-SOR-${yearStr}`;
      }
    }
    if (cardType === "client_sor" && clientId) {
      const c = clients.find(c => c.client_id === clientId);
      if (c) {
        const slug = c.legal_name
          .toUpperCase().replace(/[^A-Z0-9]+/g, "-").replace(/^-|-$/g, "")
          .slice(0, 24);
        return `${slug}-SOR-${yearStr}`;
      }
    }
    if (cardType === "coretel_sor") return `CORETEL-INTERNAL-${yearStr}`;
    return "";
  }, [cardType, supplierId, clientId, suppliers, clients, yearStr]);

  const autoName = useMemo(() => {
    if (cardType === "supplier_sor" && supplierId) {
      const s = suppliers.find(s => s.supplier_id === supplierId);
      if (s) return `${s.trading_name ?? s.legal_name} — ${yearStr}`;
    }
    if (cardType === "client_sor" && clientId) {
      const c = clients.find(c => c.client_id === clientId);
      if (c) return `${c.legal_name} — ${yearStr}`;
    }
    if (cardType === "coretel_sor") return `Coretel Internal ${yearStr}`;
    return "";
  }, [cardType, supplierId, clientId, suppliers, clients, yearStr]);

  useEffect(() => {
    if (!codeTouched) setCode(autoCode);
  }, [autoCode, codeTouched]);
  useEffect(() => {
    if (!nameTouched) setDisplayName(autoName);
  }, [autoName, nameTouched]);

  // Reset party when type changes
  useEffect(() => {
    setSupplierId("");
    setClientId("");
  }, [cardType]);

  // -------------------------------------------------------------------------
  const canSave = (
    canManage &&
    !saving &&
    code.trim().length > 0 &&
    displayName.trim().length > 0 &&
    effectiveFrom &&
    (cardType === "coretel_sor" ||
     (cardType === "supplier_sor" && !!supplierId) ||
     (cardType === "client_sor"   && !!clientId))
  );

  async function onSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    const payload: any = {
      rate_card_code:   code.trim(),
      display_name:     displayName.trim(),
      description:      description.trim() || null,
      rate_card_type:   cardType,
      effective_from:   effectiveFrom,
      is_default:       false,
      is_active:        true,
      supplier_id:      cardType === "supplier_sor" ? supplierId : null,
      client_id:        cardType === "client_sor"   ? clientId   : null,
    };
    const { data, error } = await supabase
      .from("rate_cards")
      .insert(payload)
      .select("rate_card_id")
      .single();
    setSaving(false);
    if (error) {
      setError(error.message);
      return;
    }
    if (data?.rate_card_id) {
      navigate(`/rates/${data.rate_card_id}`);
    } else {
      navigate("/rates");
    }
  }

  // -------------------------------------------------------------------------
  if (!canManage) {
    return (
      <div className="p-6 max-w-[700px] mx-auto">
        <Card>
          <div className="p-6 text-text-muted">
            You need manager-or-above access to create rate cards.
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-[820px] mx-auto">
      <Link to="/rates" className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3">
        <ArrowLeft size={14}/> Rate Cards
      </Link>
      <header className="mb-5">
        <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
          <BookOpen size={22}/> New Rate Card
        </h1>
        <div className="text-text-muted text-[13px] mt-1">
          Create a card to hold related rate lines. Supplier cards capture cost
          rates from external providers; Coretel SoR holds our master sell
          rates; client cards override the master for a specific client.
        </div>
      </header>

      <Card>
        <div className="p-5 space-y-4">
          <Field label="Card type">
            <div className="flex gap-2 flex-wrap">
              {(["supplier_sor", "coretel_sor", "client_sor"] as CardType[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setCardType(t)}
                  className={`px-3 py-1.5 rounded border text-[13px] transition-colors ${
                    cardType === t
                      ? "bg-accent text-white border-accent"
                      : "border-line text-text-muted hover:border-accent hover:text-accent"
                  }`}
                >
                  {t === "supplier_sor" && "Supplier"}
                  {t === "coretel_sor"  && "Coretel SoR"}
                  {t === "client_sor"   && "Client-specific"}
                </button>
              ))}
            </div>
          </Field>

          {cardType === "supplier_sor" && (
            <Field label="Supplier">
              <Select value={supplierId} onChange={e => setSupplierId(e.target.value)}>
                <option value="">— pick a supplier —</option>
                {suppliers.map(s => (
                  <option key={s.supplier_id} value={s.supplier_id}>
                    {s.legal_name}{s.trading_name && ` (${s.trading_name})`}
                  </option>
                ))}
              </Select>
              {suppliers.length === 0 && (
                <div className="text-[11px] text-text-muted mt-1">
                  No active suppliers yet. Add one at <Link to="/suppliers/new" className="text-accent">/suppliers/new</Link>.
                </div>
              )}
            </Field>
          )}

          {cardType === "client_sor" && (
            <Field label="Client">
              <Select value={clientId} onChange={e => setClientId(e.target.value)}>
                <option value="">— pick a client —</option>
                {clients.map(c => (
                  <option key={c.client_id} value={c.client_id}>
                    {c.client_code} · {c.legal_name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Code" hint="Short identifier (auto-suggested)">
              <Input
                value={code}
                onChange={e => { setCode(e.target.value); setCodeTouched(true); }}
                placeholder="e.g. BOOM-SOR-2026"
              />
            </Field>
            <Field label="Effective from">
              <Input
                type="date"
                value={effectiveFrom}
                onChange={e => setEffectiveFrom(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Display name">
            <Input
              value={displayName}
              onChange={e => { setDisplayName(e.target.value); setNameTouched(true); }}
              placeholder="e.g. BOOM Logistics — 2026"
            />
          </Field>

          <Field label="Description" hint="Optional notes — what's covered, where it came from">
            <Textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="e.g. EWP wet-hire rates from BOOM 2026 quote bundle. Add 30m and 40m Travel Tower lines as journals."
            />
          </Field>

          {error && (
            <div className="text-[12px] text-lost border border-lost/30 bg-lost/[0.05] rounded px-3 py-2">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Link to="/rates">
              <Button variant="secondary">Cancel</Button>
            </Link>
            <Button onClick={onSave} disabled={!canSave} icon={saving ? undefined : Save}>
              {saving ? <Spinner size={14}/> : "Create card"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="mt-3 text-[11px] text-text-dim">
        After creating, you'll land on the detail screen and can journal lines (e.g. "EWP — 30m Travel Tower per hour", "EWP — 40m Travel Tower per hour") with their cost prices.
      </div>
    </div>
  );
}
